package ui.home

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.*
import androidx.compose.animation.slideInHorizontally
import androidx.compose.animation.slideOutHorizontally
import androidx.compose.foundation.Image
import androidx.compose.foundation.clickable
import androidx.compose.foundation.hoverable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.dp
import cafe.adriel.voyager.navigator.tab.CurrentTab
import cafe.adriel.voyager.navigator.tab.TabNavigator
import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.toastmanager.Toast
import com.essntl.toastmanager.ToastManager
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.link.NavLinkDivider
import com.essntl.uikit.components.link.NavLinkRow
import com.essntl.uikit.components.utils.esIconPainterResource
import com.essntl.uikit.theme.esTypography
import navigation.home.allitinerary.AllItineraryScreenTab
import navigation.home.clients.allclients.AllClientsScreenTab
import navigation.home.crm.CRMScreenTab
import navigation.home.dashboard.DashboardScreenTab
import navigation.home.edititinerary.EditItineraryScreenTab
import navigation.home.messages.MessagesScreenTab
import navigation.home.proposal.allproposal.AllProposalScreenTab
import navigation.home.service.allservice.AllServiceScreenTab
import navigation.home.settings.SettingsScreenTab
import navigation.home.task.alltasks.AllTasksScreenTab
import navigation.utils.NavLinkScreen
import navigation.utils.rememberNavLinks
import navigation.utils.toNavigator
import ui.home.components.NavSidebar

@Composable
fun HomeScreen(
    state: HomeState,
    onEvent: (HomeEvent) -> Unit,
    toastManager: ToastManager,
    loadingManager: LoadingManager,
) {
    DisposableEffect(Unit) {
        onEvent(HomeEvent.CollectQbAuth)
        onEvent(HomeEvent.CollectNotifications)

        onDispose {
            onEvent(HomeEvent.UnsubscribeFromNotifications)
            onEvent(HomeEvent.UnsubscribeFromQbAuth)
        }
    }

    val rotationAnimation = rememberInfiniteTransition()

    val rotationAngle by rotationAnimation.animateFloat(
        initialValue = 0f,
        targetValue = 360f,
        animationSpec =
            infiniteRepeatable(
                animation =
                    tween(
                        durationMillis = 1000,
                        easing = LinearEasing,
                    ),
                repeatMode = RepeatMode.Restart,
            ),
    )

    val density = LocalDensity.current

    val isLoading = rememberSaveable { mutableStateOf(false) }

    var isFirstTimeDialogOpen by remember { mutableStateOf(false) }

    var parentNavigator by remember { mutableStateOf<TabNavigator?>(null) }

    TabNavigator(
        tab = AllProposalScreenTab,
    ) { tabNavigator ->

        LaunchedEffect(tabNavigator) {
            parentNavigator = tabNavigator
        }

        val childNavigator = parentNavigator?.current?.toNavigator()

        LaunchedEffect(state.openFirstTimeDialog) {
            if (state.openFirstTimeDialog)
                isFirstTimeDialogOpen = true
        }

        FirstTimeDialog(
            openDialog = isFirstTimeDialogOpen,
            onAddServices = { parentNavigator?.current = AllServiceScreenTab },
            onDismissRequest = { isFirstTimeDialogOpen = false },
        )

        LaunchedEffect(Unit) {
            toastManager.clickedToastFlow
                .collect { clickedToast ->
                    if (
                        clickedToast is Toast.Message &&
                        parentNavigator?.current != MessagesScreenTab()
                    ) {
                        parentNavigator?.current = MessagesScreenTab(
                            conversationId = clickedToast.conversationId,
                        )
                    }
                }
        }

        LaunchedEffect(Unit) {
            loadingManager.isLoadingFlow.collect {
                isLoading.value = it
            }
        }

        val navItems = rememberNavLinks(childNavigator)

        var selectedNavItem by rememberSaveable {
            mutableStateOf(NavSidebarItem.Dashboard)
        }

        val badgeCounts =
            remember(state) {
                mapOf(NavSidebarItem.Tasks to state.getFilteredTodayTodoTask.size)
            }

        var isSidebarFixed by rememberSaveable {
            mutableStateOf(true)
        }

        var isSidebarVisible by rememberSaveable {
            mutableStateOf(true)
        }


        LaunchedEffect(childNavigator?.lastItem) {
            val screen = childNavigator?.lastItem
            if (screen is NavLinkScreen) {
                isSidebarFixed = screen.navLinkState.isNavSidebarFixedByDefault
            }
        }

        LaunchedEffect(selectedNavItem) {
            val screen =
                when (selectedNavItem) {
                    NavSidebarItem.Dashboard ->
                        DashboardScreenTab

                    NavSidebarItem.Services ->
                        AllServiceScreenTab

                    NavSidebarItem.Proposals ->
                        AllProposalScreenTab

                    NavSidebarItem.Itineraries ->
                        AllItineraryScreenTab

                    NavSidebarItem.Concierge ->
                        EditItineraryScreenTab()

                    NavSidebarItem.Tasks ->
                        AllTasksScreenTab

//                    NavSidebarItem.Suppliers ->
//                        AllSupplierScreenNav
//
//                    NavSidebarItem.TAgents ->
//                        AllTravelAgentsScreenNav
//
                    NavSidebarItem.Clients ->
                        AllClientsScreenTab

                    NavSidebarItem.Messages ->
                        MessagesScreenTab()

                    NavSidebarItem.CRM -> CRMScreenTab

                    NavSidebarItem.Settings ->
                        SettingsScreenTab
                }

            parentNavigator?.current = screen
        }

        CompositionLocalProvider(
            LocalDensity provides density,
        ) {
            Column(
                modifier = Modifier
                    .fillMaxSize(),
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier
                        .fillMaxWidth(),
                ) {
                    Image(
                        painter = esIconPainterResource(EsIcons.Logo),
                        contentDescription = "Logo",
                        contentScale = ContentScale.Fit,
                        modifier = Modifier
                            .width(80.dp)
                            .height(60.dp)
                            .rotate(if (isLoading.value) rotationAngle else 0F)
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null,
                            ) {
                                isSidebarFixed = !isSidebarFixed
                            },
                    )

                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                    ) {
                        Text(
                            text = "Odia",
                            style = MaterialTheme.esTypography.p2Regular14,
                            modifier =
                                Modifier
                                    .padding(end = 8.dp)
                                    .clickable(
                                        interactionSource = remember { MutableInteractionSource() },
                                        indication = null,
                                    ) {
                                        isSidebarFixed = !isSidebarFixed
                                    },
                        )

                        NavLinkDivider()

                        NavLinkRow(
                            items = navItems,
                            onItemClicked = { index, _ ->
                                val navigator = childNavigator
                                    ?: return@NavLinkRow

                                val toPopItemsCount = navigator.items.lastIndex - index

                                if (toPopItemsCount <= 0) {
                                    return@NavLinkRow
                                }

                                val lastItem =
                                    navItems.lastOrNull()
                                        ?: return@NavLinkRow

                                if (!lastItem.canPop()) {
                                    return@NavLinkRow
                                }

                                repeat(toPopItemsCount) {
                                    if (navigator.canPop) {
                                        navigator.pop()
                                    }
                                }
                            },
                            modifier = Modifier,
                        )
                    }
                }

                val interactionSource = remember { MutableInteractionSource() }
                val isHovered by interactionSource.collectIsHoveredAsState()

                LaunchedEffect(isHovered) {
                    if (!isHovered) {
                        isSidebarVisible = false
                    }
                }

                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f)
                        .hoverable(interactionSource)
                        .pointerInput(isSidebarFixed) {
                            if (!isSidebarFixed) {
                                awaitPointerEventScope {
                                    while (true) {
                                        val event = awaitPointerEvent()
                                        val change = event.changes.firstOrNull()
                                        if (change != null && event.type == PointerEventType.Move) {
                                            if (change.position.x < 20.dp.toPx()) {
                                                isSidebarVisible = true
                                            } else if (change.position.x > 80.dp.toPx()) {
                                                isSidebarVisible = false
                                            }
                                        }
                                    }
                                }
                            }
                        },
                ) {
                    Row(
                        modifier = Modifier
                            .matchParentSize(),
                    ) {
                        AnimatedVisibility(
                            visible = isSidebarFixed,
                        ) {
                            NavSidebar(
                                selectedItem = selectedNavItem,
                                onItemSelected = {
                                    if (it == selectedNavItem) return@NavSidebar
                                    selectedNavItem = it
                                },
                                getProfile = state.getProfile,
                                logout = {
                                    onEvent(HomeEvent.Logout)
                                },
                                badgeCounts = badgeCounts,
                                modifier = Modifier,
                            )
                        }

                        CurrentTab()
                    }

                    if (!isSidebarFixed) {
                        androidx.compose.animation.AnimatedVisibility(
                            visible = isSidebarVisible,
                            enter =
                                slideInHorizontally(
                                    initialOffsetX = { -it },
                                ),
                            exit =
                                slideOutHorizontally(
                                    targetOffsetX = { -it },
                                ),
                        ) {
                            Box {
                                Box(
                                    modifier =
                                        Modifier
                                            .matchParentSize()
                                            .offset(y = 6.dp)
                                            .shadow(elevation = 6.dp),
                                )

                                NavSidebar(
                                    selectedItem = selectedNavItem,
                                    onItemSelected = {
                                        if (it == selectedNavItem) return@NavSidebar
                                        selectedNavItem = it
                                    },
                                    getProfile = state.getProfile,
                                    logout = {
                                        onEvent(HomeEvent.Logout)
                                    },
                                    badgeCounts = badgeCounts,
                                    modifier = Modifier,
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}
